import React, { useEffect, useRef, type ReactNode } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';

import { canScopeRequestCredits, canScopeShowResetDate } from '../../common/credits-backend/scope';
import type { CreditDenialScope } from '../../common/credits-backend/types';
import type { CreditsBannerState } from '../../common/credits-ui-state/types';
import { RovoCreditsIntlProvider } from '../../i18n';
import { useCreditsRequestContext } from '../../provider/credits-request-context';
import { useCreditsStateContext } from '../../provider/credits-state-context';
import type { CreditsBannerProps } from '../../ui/credits-banner';
import { formatResetDate } from '../credits-banner-content/format-reset-date';
import { messages } from '../credits-banner-content/messages';
import { getDepletedDescriptor, normalizeAgentName } from '../credits-banner-content/scope';
import { useDismissalPersistence } from '../credits-banner-content/utils';
import { RequestCreditsAction } from '../request-credits-action';

const styles = cssMap({
	requestLink: {
		textDecorationLine: 'underline',
	},
});

const surfaceMessages = defineMessages({
	resetDate: {
		id: 'rovo-credits.internal.credits-surface.resetDate',
		defaultMessage: 'Resets {resetDate}.',
		description: 'Separate line in a credits popup showing the localized reset date.',
	},
	requestCredits: {
		id: 'rovo-credits.internal.credits-surface.requestCredits',
		defaultMessage: 'Request credits',
		description: 'Popup link text for requesting more Rovo credits.',
	},
	title: {
		id: 'rovo-credits.internal.credits-surface.title',
		defaultMessage: 'Rovo credits',
		description: 'Title of a notification or popup explaining Rovo credit availability.',
	},
});

type SurfaceContent = {
	title: string;
	formattedResetDate?: string;
	canRequestCredits: boolean;
	scope?: CreditDenialScope;
	agentName?: string;
	isWarning: boolean;
	dismiss: () => void;
	description: ReactNode;
	details: ReactNode;
};

type CreditsSurfaceProps = Omit<CreditsBannerProps, 'xcss'> & {
	children: (content: SurfaceContent) => ReactNode;
};

const Surface = ({
	state,
	resetDate,
	agentName,
	onDismiss,
	onError,
	children,
}: CreditsSurfaceProps & { state: CreditsBannerState }): React.JSX.Element | null => {
	const intl = useIntl();
	const request = useCreditsRequestContext();
	const { formatMessage } = intl;
	const isWarning = state.balanceState === 'warning';
	const variant = `${state.balanceState}-${state.depletionBehavior}-${state.scope}`;
	const previousVariant = useRef(variant);
	const { isDismissed, dismissBanner, showBanner } = useDismissalPersistence(isWarning, onError);

	useEffect(() => {
		if (variant !== previousVariant.current && isDismissed) {
			showBanner();
		}
		previousVariant.current = variant;
	}, [variant, isDismissed, showBanner]);

	if (state.depletionBehavior === null || isDismissed) {
		return null;
	}

	const { scope } = state;
	const formattedResetDate = canScopeShowResetDate(scope)
		? formatResetDate(intl, resetDate ?? state.resetDate)
		: undefined;
	const canRequestCredits =
		state.canRequestCredits === true && canScopeRequestCredits(scope) && !request?.isUnavailable;
	const trimmedAgentName = normalizeAgentName(agentName);
	const dismiss = () => {
		dismissBanner();
		onDismiss?.();
	};
	const description = (
		<Stack space="space.200" alignInline="start">
			<Stack>
				<Text>
					{formatMessage(
						isWarning
							? messages.warningWithoutResetDate
							: getDepletedDescriptor({
									scope,
									agentName: trimmedAgentName,
									hasContext: false,
									hasResetDate: false,
								}),
						{ agentName: trimmedAgentName },
					)}
				</Text>
				{formattedResetDate ? (
					<Text>{formatMessage(surfaceMessages.resetDate, { resetDate: formattedResetDate })}</Text>
				) : null}
			</Stack>
			{canRequestCredits ? (
				<RequestCreditsAction isUnderlined>
					<Box as="span" xcss={styles.requestLink}>
						{formatMessage(surfaceMessages.requestCredits)}
					</Box>
				</RequestCreditsAction>
			) : null}
		</Stack>
	);
	const details = (
		<Stack space="space.100">
			{description}
			{isWarning ? (
				<Button appearance="subtle" onClick={dismiss}>
					{formatMessage(messages.dismissBanner)}
				</Button>
			) : null}
		</Stack>
	);

	return (
		<>
			{children({
				title: formatMessage(surfaceMessages.title),
				formattedResetDate,
				canRequestCredits,
				scope,
				agentName: trimmedAgentName,
				isWarning,
				dismiss,
				description,
				details,
			})}
		</>
	);
};

/** Shared provider state and copy for the alternative credits presentations. */
export const CreditsSurface = (props: CreditsSurfaceProps): React.JSX.Element | null => {
	const credits = useCreditsStateContext();
	if (credits?.bannerState == null) {
		return null;
	}
	return (
		<RovoCreditsIntlProvider>
			<Surface {...props} state={credits.bannerState} />
		</RovoCreditsIntlProvider>
	);
};
